import React, { useContext, useEffect, useState, useRef } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';

import CustomButton from '../Componentization/CustomButton';
import CustomText from '../../styles/CustomText';
import useWallet from '../../hooks/useWallet';
import useSecureStorage from '../../hooks/useSecureStorage';
import useQssMnemonic from '../../hooks/useQssMnemonic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/free-solid-svg-icons';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkQssMnemonicName, RootState } from '../../store';

import i18next from 'i18next'
import moment from 'moment';
import WebApp from '@twa-dev/sdk';

/**
 * @brief qSS 지갑생성 니모닉생성 및 이름입력 화면
 * @returns 
 */
const QssCreateSecretPhraseConfirmation: React.FC = () => {
	const { theme } = useContext(ThemeContext)
	let navigate = useNavigate();
	let dispatch = useDispatch();
	const { t } = useTranslation();

	const { makeWallet, restoreWallet } = useWallet()
	const { 
		storeEncryptedData, 
		storeNetworkEncryptedData,
		setLocalMnemonicHash,
		setBlockChain, 
		getBlockChain, 
		setNetworkCurrency, 
		setWalletAddress, 
		setNetworkWalletAddress,
		overwriteWalletData,
		logOut 
	} = useSecureStorage()
	const { checkEmptyQssMnemonic, checkOldQssData } = useQssMnemonic()
	
	const { getNetworkInfo } = useContext(DefaultTokenContext);

	const [mnemonic, setMnemonic] = useState<string[]>([])
	const [loading, setLoading] = useState<boolean>(true)
	const [isComplate, setIsComplate] = useState<boolean>(false)
	const [isMnemonicNameValid, setIsMnemonicNameValid] = useState(false)
	const [mnemonicName, setMnemonicName] = useState(moment().format('YYYY-MM-DD HH:mm') + " Default QSS")
	const [mnemonicNameHelperMessage, setMnemonicNameHelperMessage] = useState('')

	const pincode = useSelector((state: RootState) => state.pincode.value);

	// 버튼 연속 클릭 방지
	const isClickedRef = useRef(false);

	useEffect(() => {
		initializeWallet()
	}, [])

	useEffect(() => {
		validateMnemonicName(mnemonicName)
	}, [mnemonicName])

	const initializeWallet = async () => {
		try {
			const { privateKey, publicKey, address, mnemonic } = await makeWallet(process.env.REACT_APP_NETWORK_NAME ?? 'qChain')

			if (null == privateKey || null == publicKey || null == address || null == mnemonic) {
				setIsComplate(false)
				alert('Error Make mnemonic. please restart');
				navigate('/CreateWallet')
				return
			}

			// 네트워크 설정 정보
			const defaultNetworks = await getNetworkInfo('')
			console.log(`defaultNetworks : `, defaultNetworks)
			const activeNetworks = defaultNetworks.filter(network => network.is_active);
			console.log(`활성화된 네트워크 목록:`, activeNetworks);

			// 활성화된 네트워크 별로 지갑 생성 및 저장
			let walletResults: any[] = [];
			for (const network of activeNetworks) {
				const result = await saveNetworkWallet(network.symbol, mnemonic, pincode);
				if (result.result === false || result.address == null) {
					console.error(`❌ ${network.symbol} 지갑 복구 실패`);
					setIsComplate(false)
					logOut();
					alert('Error Make mnemonic. please restart');
					navigate(`/CreateWallet`, { replace: true, state: { } });
					return
				}
				
				// 네트워크 지갑 주소 저장
				await setNetworkWalletAddress(result.address, network.symbol);
				walletResults.push({ network: network.symbol, address: result.address });
			}
			
			// 기본 네트워크 선택 및 저장
			const primaryNetwork = activeNetworks.find(net => net.symbol === process.env.REACT_APP_NETWORK_NAME);
			if (!primaryNetwork) {
				console.error('❌ 기본 네트워크 설정 실패');
				logOut();
				alert('Error setting default network wallet. Please restart.');
				navigate(`/CreateWallet`, { replace: true });
				return;
			}

			// 로컬 스토리지에 기본 네트워크 설정
			await overwriteWalletData(primaryNetwork.symbol);

			// 로컬 스토리지에 기본 지갑주소 설정
			await setWalletAddress(walletResults.find(w => w.network === primaryNetwork.symbol)?.address);

			// 현재 기본 블록체인 저장
			await setBlockChain(primaryNetwork.symbol);

			// 현재 기본 네트워크의 메인 currency 저장
			await setNetworkCurrency(primaryNetwork.currency);

			// 로컬 니모닉 해시 저장
			await setLocalMnemonicHash(mnemonic);

			setMnemonic(mnemonic.split(' '))
			setIsComplate(true)
		} catch (error) {
			console.error('Error initializing wallet:', error)
		} finally {
			setLoading(false)
		}
	}

	/**
	 * 네트워크 지갑 생성 및 저장
	 * @param network 
	 * @param mnemonic 
	 * @param pinCode 
	 */
	const saveNetworkWallet = async (network: string, mnemonic: string, pinCode: string) => {
		try {
			const { privateKey, publicKey, address } = await restoreWallet(mnemonic, network)

			if (network == null || address == null|| null == privateKey || null == publicKey || null == mnemonic) {
				return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false}
			}

			const result = await storeNetworkEncryptedData(network, privateKey, publicKey, mnemonic, pinCode);
			if(false === result) {
				return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false}
			}

			return {privateKey, publicKey, address, mnemonic, result:true} 
		} catch (error) {
			console.error('Error save network wallet:', error)
		}
		return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false} 
	}

	// 다음 화면 이동
	const handleNext = async () => {
		try {
			if (true === isComplate) {
				if (isClickedRef.current) {
					console.error('버튼 연속 클릭 차단')
					return;
				}
				isClickedRef.current = true;

				if(false === isMnemonicNameValid) {
					alert(i18next.t('qss_create_secret_phrase_confirm_screen_message_09'))
					return
				}

				// qSS 저장될 니모닉 이름 redux에 임시 저장
				dispatch(checkQssMnemonicName(mnemonicName))

				// 이전 QSS 데이터가 없을 경우 새 QSS 비밀번호 설정
				const selNetwork = await getBlockChain()
				const isEmpty = await checkEmptyQssMnemonic(selNetwork);
				if (true === isEmpty) {
					// 저장된 qss 니모닉 데이터 없음
					// QssNewPassword 페이지로 이동
					console.log('qss에 저장된 니모닉코드 없음');
					setTimeout(() => {
						// @ts-ignore
						navigate(`/QssNewPassword`, { replace: true, state: { } });
					}, 500)

					return;
				} 
				
				// 이전 형식의 QSS 데이터 있을 경우 알림
				const isOldData = await checkOldQssData(selNetwork);
				if (true === isOldData) {
					// 이전 저장된 qss 니모닉 데이터 없음
					WebApp.showAlert(t('wallet_qss_recovery_message_14'));
					return
				}
				
				// 이전 저장된 qss 니모닉 데이터 있음
				// QssPassword 페이지로 이동
				console.log('qss에 저장된 니모닉코드 있음');
				setTimeout(() => {
					// @ts-ignore
					navigate(`/QssPassword`, { replace: true, state: { } });
				}, 500)
			} else {
				console.warn('QssCreateSecretPhraseConfirmation wait initializeWallet : ', isComplate);
			}
		} catch (error) {
			console.warn('Error QSS mnemonic save:', error);
		} finally {
			isClickedRef.current = false;
		}
	}

	const handleCopy = (): void => {
		const code = mnemonic.join(' ')
		if (code) {
			navigator.clipboard.writeText(code)
				.then(() => {
					alert(i18next.t('verify_recovery_syntax_message_10'));
				})
				.catch(err => {
					console.error('Fail Copy mnemonic:', err);
				});
		}
	};

	const validateMnemonicName = (address: string) => {
		let isValid = true
		let message = ''

		if (address.length === 0) {
			isValid = false
			message = i18next.t('qss_create_secret_phrase_confirm_screen_message_07')
		} else if (address.length > 40) {
			isValid = false
			message = i18next.t('qss_create_secret_phrase_confirm_screen_message_08')
		}

		setIsMnemonicNameValid(isValid)
		setMnemonicNameHelperMessage(message)
	}

	const styles = {
		// scrollContainer: {
		// 	width: "100%",
		// } as React.CSSProperties,
		header: {
			width: "100%",
		} as React.CSSProperties,
		body: {
			width: "100%",
		} as React.CSSProperties,
		title: {
			fontSize: 24,
			textAlign: 'left',
			marginBottom: 10
		} as React.CSSProperties,
		subTitle: {
			fontSize: 14,
			textAlign: 'left',
			marginBottom: 5
		} as React.CSSProperties,
		phraseContainer: {
			display: "grid",
			gridTemplateColumns: "repeat(2, 1fr)",
			gap: "10px"
		} as React.CSSProperties,
		warning: {
			marginTop: 15,
			display: "flex",
			alignItems: 'center',
			justifyContent: 'flex-start'
		} as React.CSSProperties,
		warningText2: {
			fontSize: 14,
			textAlign: 'left',
			marginTop: 5
		} as React.CSSProperties,
		footer: {
			width: '100%'
		} as React.CSSProperties,
		button: {
			padding: '12px',
			borderRadius: '10px',
			marginBottom: '12px',
			width: '100%',
			display: "flex",
			alignItems: 'center',
			height: '54px',
			justifyContent: 'center',
		} as React.CSSProperties,
		buttonText: {
			color: theme.static_white,
			fontSize: 16
		} as React.CSSProperties,
		icon: {
			width: 18,
			height: 18,
			marginRight: 5
		} as React.CSSProperties,
		inputRow: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 26
		} as React.CSSProperties,
		helperText: {
			color: 'red',
			fontSize: 12,
			marginTop: 5,
			marginLeft: 0,
			height: 1
		} as React.CSSProperties,
		inputMnemonicName: {
			width: "100%",
			borderTop: "none",
			borderRight: "none",
			borderLeft: "none",
			outline: "none",
			fontSize: 16,
			height: "40px",
			background: "inherit"
		} as React.CSSProperties,
	}

	return (
		<div className='container' style={{ backgroundColor: theme.background_normal }}>
			{/* <div style={styles.scrollContainer}> */}
			<div style={styles.header}>
				<CustomText weight="medium" style={{ ...styles.title, color: theme.label_strong }}>
					{t('qss_create_secret_phrase_confirm_screen_message_01')}
				</CustomText>
				<CustomText style={{ ...styles.subTitle, color: theme.label_strong }}>
					{t('qss_create_secret_phrase_confirm_screen_message_02')}
				</CustomText>
			</div>

			<div style={styles.body}>
				<CustomText style={{ ...styles.warningText2, color: theme.label_strong }}>
					{t('qss_create_secret_phrase_confirm_screen_message_03')}
				</CustomText>
				<CustomText style={{ ...styles.warningText2, color: theme.label_strong }}>
					{t('qss_create_secret_phrase_confirm_screen_message_04')}
				</CustomText>
				<CustomText style={{ ...styles.warningText2, color: theme.label_alternative }}>
					{t('qss_create_secret_phrase_confirm_screen_message_05')}
				</CustomText>
				<div style={styles.inputRow}>
					<input
						value={mnemonicName}
						onChange={(e) => setMnemonicName(e.target.value)}
						placeholder={i18next.t('qss_create_secret_phrase_confirm_screen_message_06')}
						style={{
							...styles.inputMnemonicName,
							borderBottom: `1px solid ${theme.label_alternative}`,
							color: theme.label_strong
						}}
						aria-invalid={!isMnemonicNameValid}
					/>
					<FontAwesomeIcon icon={faPen} color={theme.static_white} />
				</div>
				<p style={styles.helperText}>
					{!isMnemonicNameValid && mnemonicName.length > 0 && (
						mnemonicNameHelperMessage
					)}
				</p>
			</div>

			<div style={styles.footer}>
				<CustomButton
					containerStyle={{ ...styles.button, backgroundColor: theme.primary_normal }}
					textStyle={styles.buttonText}
					onPress={handleNext}
					text={t('verify_recovery_syntax_message_09')}
					withBackground={true}
					backgroundColor={theme.primary_normal}
				/>
			</div>
			{/* </div> */}
		</div>
	)
}

export default QssCreateSecretPhraseConfirmation
